import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import useNotification from "../../hooks/useNotification";
import { registerWalletByCode } from "../../services/api/kyc.service";
import { useAuth } from "../../contexts/AuthContext";

export function SubWalletRegistration() {
  const { token, walletId } = useParams();
  const navigate = useNavigate();
  const wallet = useWallet();
  const { connected, connecting, publicKey, signMessage } = wallet;
  const { notify } = useNotification();
  const { signNonce, signInViaLedger, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Handle wallet connection and authentication
  useEffect(() => {
    if (connected && publicKey && !isAuthenticated) {
      handleAuthentication();
    }
    // eslint-disable-next-line
  }, [connected, publicKey]);

  // Handle wallet registration after authentication
  useEffect(() => {
    if (connected && publicKey && isAuthenticated) {
      handleRegistration();
    }
    // eslint-disable-next-line
  }, [isAuthenticated]);

  const isLedger = !signMessage || wallet.wallet?.adapter?.name === "Ledger";

  const handleAuthentication = async () => {
    try {
      setStatus("loading");
      const authenticated = isLedger ? await signInViaLedger() : await signNonce();
      if (!authenticated) {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      setStatus("error");
      notify("Failed to authenticate wallet. Please try again.", "error");
      console.error("Failed to authenticate wallet:", error);
    }
  };

  const handleRegistration = async () => {
    if (!publicKey || !token || !walletId) return;

    try {
      setStatus("loading");
      setErrorMessage(""); // Clear any previous error
      await registerWalletByCode(walletId, token);
      setStatus("success");
      notify("Wallet successfully linked!", "success");
    } catch (error: any) {
      setStatus("error");
      const message = error.response?.data?.message || "Failed to link wallet. Please try again.";
      setErrorMessage(message);
      notify(message, "error");
      console.error("Failed to link wallet:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100">
      <div className="w-full px-4 md:px-0 md:max-w-4xl mx-auto pt-8">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>

        <div className="bg-white border rounded-xl shadow-lg p-6 md:p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Link Your Sub-wallet
            </h1>
            <p className="text-gray-600">
              Connect your wallet to link it as a sub-wallet to the main account
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  Token: <span className="font-mono">{token}</span>
                </p>
              </div>

              {!connected && status === "idle" && (
                <div className="flex justify-center">
                  <WalletMultiButton className="bg-yellow-500 hover:bg-yellow-600 text-black" />
                </div>
              )}

              {(connecting || status === "loading") && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="animate-spin text-yellow-500" size={24} />
                  <span className="ml-2 text-gray-600">
                    {connecting ? "Connecting wallet..." : "Linking wallet..."}
                  </span>
                </div>
              )}

              {status === "success" && (
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center text-green-500">
                    <CheckCircle2 size={32} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Wallet Successfully Linked!
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Your wallet {publicKey?.toString().slice(0, 4)}...
                      {publicKey?.toString().slice(-4)} has been linked as a
                      sub-wallet
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/wallet-management")}
                    className="w-full py-3 px-4 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 
                             transition-colors duration-200"
                  >
                    Go to Wallet Management
                  </button>
                </div>
              )}

              {status === "error" && (
                <div className="text-center space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 relative overflow-hidden">
                    {/* Decorative background pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-red-500"></div>
                      <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-red-500"></div>
                    </div>

                    <div className="relative">
                      <div className="flex items-center space-x-3 mb-3">
                        <XCircle className="text-red-500" size={24} />
                        <h3 className="text-lg font-semibold text-red-700">
                          Connection Failed
                        </h3>
                      </div>

                      <div className="pl-9"> {/* Align with icon */}
                        <p className="text-red-600 text-sm">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-3">
                    <p className="text-sm text-gray-600">
                      Try connecting your wallet again
                    </p>
                    <WalletMultiButton className="bg-yellow-500 hover:bg-yellow-600 text-black" />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Important Notes:
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• This link can only be used once</li>
                <li>• Make sure you're connecting the correct wallet</li>
                <li>• The linked wallet will have limited permissions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
